/*
 * Queues data
 *
 * - Holds the current set of queues
 * - Handles everything about queues from the dashboard
 */

import { computed, ref, reactive } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useRealtimeStore } from '@/stores/Realtime'
import { useApiStore } from '@/stores/Api'
import { usePhonebookStore } from '@/stores/Phonebook'

import logger from '@/utilities/Logger'
import globalEvents from '@/utilities/GlobalEvents'
import _i18n from '@/utilities/I18n'
import { sleep } from '@/utilities/Tools'

import type { PbxAppQueuesAllItem, PbxAppQueuesAllResponse, PbxAppQueuesCaller, PbxAppQueuesCallerState } from '@/TcxApi/PbxApp'
import type { ConfigEventCommon, PbxEventQueueEntered, PbxEventQueueLeft, PbxEventQueueMemberJoin, PbxEventQueueMemberLeft, PbxEventQueueMemberUpdate } from '@per_moeller/tcx-realtime-client'
import type { AxiosError } from 'axios'

export const useQueuesStore = defineStore('queues', () => {
	logger.debug('Queues: Initializing store')

	const i18n = _i18n.global
	const api = useApiStore()
	const realtime = useRealtimeStore()
	const ss = useSettingsStore()
	const phonebook = usePhonebookStore()

	// #region Data

	const queues = ref<PbxAppQueuesAllItem[]>([])
	const _ready = ref(false)

	/*
	 * Snapshot race guard:
	 * While a queue snapshot fetch (init or single-queue refetch) is in flight, realtime mutation
	 * events are buffered and replayed after the snapshot has been applied. Otherwise a caller that
	 * leaves a queue while the fetch is in flight would have its QUEUE_LEFT consumed against the old
	 * state and then be resurrected by the (older) snapshot - leaving a stale caller in the UI.
	 *
	 * Replaying through the real handlers cannot double-fire 'Queues:Entered'/'Queues:Left' because
	 * those emits only happen in the net-new-state branches (new caller pushed / existing caller
	 * spliced). Keep it that way - moving the emits would reintroduce double-firing on replay.
	 */
	let snapshotInFlight = false
	let initRerunRequested = false
	let initRetryTimer: ReturnType<typeof setTimeout> | null = null
	const eventBuffer: (() => void)[] = []

	// #endregion

	// #region Initializing, loading data

	realtime.configEvents.on('PBX_QUEUE_CREATED', queueCreatedEventHandler)
	realtime.configEvents.on('PBX_QUEUE_UPDATED', queueUpdatedEventHandler)
	realtime.configEvents.on('PBX_QUEUE_DELETED', queueDeletedEventHandler)
	realtime.pbxEvents.on('QUEUE_ENTERED', callerEnteredHandler)
	realtime.pbxEvents.on('QUEUE_LEFT', callerLeftHandler)
	realtime.pbxEvents.on('QUEUE_MEMBER_JOIN', memberJoin)
	realtime.pbxEvents.on('QUEUE_MEMBER_UPDATE', memberUpdate)
	realtime.pbxEvents.on('QUEUE_MEMBER_LEFT', memberLeft)
	realtime.realtimeEvents.on('Connected', init)
	globalEvents.on('Auth:LoggedOut', () => { queues.value = [] })
	globalEvents.on('StatusRequest', () => {
		globalEvents.emit('StatusReport', { key: 'queues', value: _ready.value })
	})

	if (realtime.connected) { init() } // else we do it on Realtime:Connected

	globalEvents.on('Midnight', () => { midnightRefresh() })

	function midnightRefresh() {
		logger.info('Queues: Performing midnight refresh')
		init()
	}

	/** Coerce date strings from the API into Date objects */
	function processQueueDates(queue: PbxAppQueuesAllItem) {
		for (const member of queue.members) {
			if (member.lastCallEnded) {
				member.lastCallEnded = new Date(member.lastCallEnded)
			}
		}

		for (const caller of queue.callers) {
			if (caller.entered) {
				caller.entered = new Date(caller.entered)
			}
			if (caller.answered) {
				caller.answered = new Date(caller.answered)
			}
		}
	}

	/** Update an existing queue in place so object references held by components stay valid */
	function updateQueueInPlace(existingQueue: PbxAppQueuesAllItem, newQueue: PbxAppQueuesAllItem) {
		existingQueue.name = newQueue.name
		existingQueue.number = newQueue.number
		existingQueue.dynamic = newQueue.dynamic
		existingQueue.member = newQueue.member
		existingQueue.serviceLevel = newQueue.serviceLevel
		existingQueue.serviceLevelCritical = newQueue.serviceLevelCritical
		existingQueue.priority = newQueue.priority

		// Update members array in place
		existingQueue.members.splice(0, existingQueue.members.length, ...newQueue.members.map(member => reactive(member)))

		// Update callers array in place
		existingQueue.callers.splice(0, existingQueue.callers.length, ...newQueue.callers.map(caller => reactive(caller)))
	}

	/** Replay events that arrived while a snapshot fetch was in flight */
	function drainEventBuffer() {
		for (const run of eventBuffer.splice(0)) { run() }
	}

	async function init() {
		logger.info('Queues: Loading')
		_ready.value = false

		// Collapse concurrent calls - the in-flight snapshot operation will run init() again when done
		if (snapshotInFlight) {
			initRerunRequested = true
			return
		}
		snapshotInFlight = true // Buffer realtime events until the snapshot has been applied

		try {
			await ss.ready()

			const res = await api.get<PbxAppQueuesAllResponse>('/pbx/app/queues?all=true')

			// Process dates in incoming data
			for (const queue of res.data) {
				processQueueDates(queue)
			}

			// Build set of new queue IDs for quick lookup
			const newQueueIds = new Set(res.data.map(queue => queue._id))

			// Remove queues that no longer exist (iterate backwards to safely splice)
			for (let idx = queues.value.length - 1; idx >= 0; idx--) {
				const queue = queues.value[idx]
				if (queue && !newQueueIds.has(queue._id)) {
					queues.value.splice(idx, 1)
				}
			}

			// Update existing queues or add new ones
			for (const newQueue of res.data) {
				const existingQueue = queues.value.find(queue => queue._id === newQueue._id)

				if (existingQueue) {
					// Update existing queue in place to preserve reactivity
					updateQueueInPlace(existingQueue, newQueue)
				} else {
					// Add new queue
					queues.value.push(reactive(newQueue))
				}
			}

			_ready.value = true
			globalEvents.emit('Queues:Loaded')
			logger.info(`Queues: ${queues.value.length} queues loaded`)
		}
		catch (err) {
			logger.warning(`Queues: Loading failed - ${(err as Error).message} - retrying in 10 seconds`)
			if (initRetryTimer) { clearTimeout(initRetryTimer) }
			initRetryTimer = setTimeout(() => {
				initRetryTimer = null
				if (!_ready.value && ss.isAuthenticated) { init() }
			}, 10000)
		}
		finally {
			// Always clear the flag and replay buffered events - even on failure - so handlers never stay wedged
			snapshotInFlight = false
			drainEventBuffer()

			if (initRerunRequested) {
				initRerunRequested = false
				init()
			}
		}
	}

	// #endregion

	// #region Event handling

	async function queueCreatedEventHandler(event: ConfigEventCommon) {
		// A full snapshot is in flight and will be followed by a full resync - the new queue is picked up there
		if (snapshotInFlight) {
			initRerunRequested = true
			return
		}
		snapshotInFlight = true // Buffer realtime events until the fetched queue has been applied

		try {
			const res = await api.get<PbxAppQueuesAllResponse>(`/pbx/app/queues?all=true&queue=${event.id}`)

			if (res.data.length === 0) { return } // User does not have access to this queue, ignore

			const newQueue = res.data[0] as PbxAppQueuesAllItem

			processQueueDates(newQueue)

			const addedQueue = reactive(newQueue)

			queues.value.push(addedQueue)
			globalEvents.emit('Queues:Created', addedQueue)
		}
		catch (err) {
			if ((err as AxiosError).status !== 0 && (err as AxiosError).code !== 'network') {
				globalEvents.emit('CommonErrorModal', {
					header: i18n.t('queues.errorHeader'),
					message: i18n.t('queues.errorLoadingNewlyCreatedQueueMessage').replace('%%id%%', event.id).replace('%%response%%', (err as AxiosError).message)
				})
			}
		}
		finally {
			snapshotInFlight = false
			drainEventBuffer()

			if (initRerunRequested) {
				initRerunRequested = false
				init()
			}
		}
	}

	async function queueUpdatedEventHandler(event: ConfigEventCommon) {
		// A full snapshot is in flight and will be followed by a full resync - the updated queue is picked up there
		if (snapshotInFlight) {
			initRerunRequested = true
			return
		}
		snapshotInFlight = true // Buffer realtime events until the fetched queue has been applied

		try {
			const res = await api.get<PbxAppQueuesAllResponse>(`/pbx/app/queues?all=true&queue=${event.id}`)
			if (res.data.length === 0) {
				// User does not have access to this queue anymore, remove it
				const index = queues.value.findIndex(queue => queue._id === event.id)
				if (index !== -1) {
					const queue = queues.value[index] as PbxAppQueuesAllItem

					queues.value.splice(index, 1)
					globalEvents.emit('Queues:Deleted', queue)
				}
			} else {
				// Got a queue from api
				const newQueue = res.data[0] as PbxAppQueuesAllItem

				processQueueDates(newQueue)

				const existingQueue = queues.value.find(queue => queue._id == event.id)
				if (existingQueue) {
					// And we have the queue in our list, update it in place so references held by components stay valid
					updateQueueInPlace(existingQueue, newQueue)
					globalEvents.emit('Queues:Updated', existingQueue)
				} else {
					// We don't have the queue in our list, add it
					const addedQueue = reactive(newQueue)

					queues.value.push(addedQueue)
					globalEvents.emit('Queues:Created', addedQueue)
				}
			}
		}
		catch (err) {
			if ((err as AxiosError).status !== 0 && (err as AxiosError).code !== 'network') {
				globalEvents.emit('CommonErrorModal', {
					header: i18n.t('queues.errorHeader'),
					message: i18n.t('queues.errorLoadingUpdatedQueueMessage').replace('%%id%%', event.id).replace('%%response%%', (err as AxiosError).message)
				})
			}
		}
		finally {
			snapshotInFlight = false
			drainEventBuffer()

			if (initRerunRequested) {
				initRerunRequested = false
				init()
			}
		}
	}

	function queueDeletedEventHandler(event: ConfigEventCommon) {
		if (snapshotInFlight) { // Buffer while a snapshot fetch is in flight - replayed by drainEventBuffer()
			eventBuffer.push(() => queueDeletedEventHandler(event))
			return
		}

		const index = queues.value.findIndex(queue => queue._id === event.id)
		if (index !== -1) {
			const queue = queues.value[index] as PbxAppQueuesAllItem

			queues.value.splice(index, 1)
			globalEvents.emit('Queues:Deleted', queue)
		}
	}

	function callerEnteredHandler(event: PbxEventQueueEntered) {
		if (snapshotInFlight) { // Buffer while a snapshot fetch is in flight - replayed by drainEventBuffer()
			eventBuffer.push(() => callerEnteredHandler(event))
			return
		}

		event.entered = new Date(event.entered)
		const queueId = event.queue

		const index = queues.value.findIndex(queue => queue._id === queueId)

		if (index !== -1) {
			if (event.number === '' || event.number?.toLowerCase() === 'anonymous') { event.privacy = true } // Extra check for privacy
			if (event.name?.toLowerCase() === 'anonymous') { event.privacy = true } // Extra check for privacy

			const name = phonebook.findNameFromNumber(event.number)

			if (name) { event.name = name }

			const existingCaller = queues.value[index]?.callers.find(caller => caller._id === event._id)

			if (existingCaller) {
				existingCaller.state = event.state as PbxAppQueuesCallerState
				existingCaller.entered = event.entered
				existingCaller.privacy = event.privacy
				existingCaller.name = event.name ?? existingCaller.name
				existingCaller.number = event.number ?? existingCaller.number
				existingCaller.sessionId = event.sessionId

				logger.debug(`Queues: Caller ${event.number} ${event.name} updated on queue ${queues.value[index]?.name}`)
			} else {
				const newCall: PbxAppQueuesCaller = reactive({
					_id: event._id,
					state: event.state as PbxAppQueuesCallerState,
					number: event.number,
					name: event.name ?? '',
					privacy: event.privacy,
					entered: event.entered,
					sessionId: event.sessionId
				})

				queues.value[index]!.callers.push(newCall) // eslint-disable-line @typescript-eslint/no-non-null-assertion
				logger.debug(`Queues: Caller ${event.number} ${event.name} entered queue ${queues.value[index]?.name}, now has ${queues.value[index]?.callers.length} callers`)
				globalEvents.emit('Queues:Entered', { queue: queueId, caller: newCall })
			}
		}
	}

	function callerLeftHandler(event: PbxEventQueueLeft) {
		if (snapshotInFlight) { // Buffer while a snapshot fetch is in flight - replayed by drainEventBuffer()
			eventBuffer.push(() => callerLeftHandler(event))
			return
		}

		event.entered = new Date(event.entered)
		if (event.answered) { event.answered = new Date(event.answered) }
		const queueId = event.queue

		const index = queues.value.findIndex(queue => queue._id === queueId)

		if (index !== -1) {
			const callerIndex = queues.value[index]?.callers.findIndex(caller => caller._id === event._id) as number

			if (callerIndex !== -1) {
				const call = queues.value[index]?.callers[callerIndex] as PbxAppQueuesCaller

				queues.value[index]?.callers.splice(callerIndex, 1)
				call.state = event.state as PbxAppQueuesCallerState
				call.answered = event.answered
				call.answeredByExtension = event.answeredByExtension
				call.answeredByNumber = event.answeredByNumber
				call.sessionId = event.sessionId

				logger.debug(`Queues: Caller ${event.number} - ${event.name} left queue ${queues.value[index]?.name}, now has ${queues.value[index]?.callers.length} callers`)
				globalEvents.emit('Queues:Left', { queue: queueId, caller: call, index: callerIndex })
			} else {
				// Expected when the caller was already removed by a snapshot refresh - logged to make any real drift visible
				logger.debug(`Queues: Caller ${event.number} - ${event.name} left queue ${queues.value[index]?.name} but was not in the local caller list`)
			}
		}
	}

	function memberJoin(event: PbxEventQueueMemberJoin) {
		if (snapshotInFlight) { // Buffer while a snapshot fetch is in flight - replayed by drainEventBuffer()
			eventBuffer.push(() => memberJoin(event))
			return
		}

		if (event.data.lastCallEnded) { event.data.lastCallEnded = new Date(event.data.lastCallEnded) }
		const index = queues.value.findIndex(queue => queue._id === event._id)

		if (index !== -1) {
			queues.value[index]?.members.push(reactive(event.data))
			globalEvents.emit('Queues:MemberJoin', { queue: event._id, member: event.data })
			logger.debug(`Queues: Member ${event.data._id} ${event.data.number || ''} joined queue ${queues.value[index]?.name}`)
		}
	}

	function memberUpdate(event: PbxEventQueueMemberUpdate) {
		if (snapshotInFlight) { // Buffer while a snapshot fetch is in flight - replayed by drainEventBuffer()
			eventBuffer.push(() => memberUpdate(event))
			return
		}

		if (event.data.lastCallEnded) { event.data.lastCallEnded = new Date(event.data.lastCallEnded) }
		const index = queues.value.findIndex(queue => queue._id === event._id)

		if (index != -1) {
			const memberIndex = queues.value[index]?.members.findIndex(member => member._id === event.data._id) as number

			if (memberIndex !== -1 && queues.value[index]) {
				queues.value[index].members[memberIndex] = reactive(event.data)
				globalEvents.emit('Queues:MemberUpdate', { queue: event._id, member: event.data, index: memberIndex })
				logger.debug(`Queues: Member ${event.data._id} ${event.data.number || ''} updated in queue ${queues.value[index].name}`)
			}
		}
	}

	function memberLeft(event: PbxEventQueueMemberLeft) {
		if (snapshotInFlight) { // Buffer while a snapshot fetch is in flight - replayed by drainEventBuffer()
			eventBuffer.push(() => memberLeft(event))
			return
		}

		if (event.data.lastCallEnded) { event.data.lastCallEnded = new Date(event.data.lastCallEnded) }
		const index = queues.value.findIndex(queue => queue._id === event._id)

		if (index !== -1) {
			const memberIndex = queues.value[index]?.members.findIndex(member => member._id === event.data._id) as number

			if (memberIndex != -1 && queues.value[index]) {
				queues.value[index].members.splice(memberIndex, 1)
				globalEvents.emit('Queues:MemberLeft', { queue: event._id, member: event.data, index: memberIndex })
				logger.debug(`Queues: Member ${event.data._id} ${event.data.number || ''} left queue ${queues.value[index].name}`)
			}
		}
	}

	// #endregion

	// #region public APIs

	/** Wait for queues to be ready */
	async function ready() {
		if (_ready.value) { return }

		while (!_ready.value) { await sleep(250) }
	}

	/** Is queues ready */
	const isReady = computed(() => { return _ready.value })

	/** Get list of queues for list usage */
	const list = computed(() => {
		return queues.value.map(queue => {
			return { value: queue._id, name: queue.name, label: queue.name + (queue.number ? ` (${queue.number})` : '') }
		})
	})

	/**
	 * Get a queue by id
	 * @param id Queue id
	 */
	function getQueue(id: string) {
		return queues.value.find(queue => queue._id === id)
	}

	// #endregion

	return { isReady, ready, queues, list, getQueue }
})
